import { createStore, SetState } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { AnalyticsTarget } from '~/modules/analytics/analyticsTarget';
import { GtmEventType } from '~/modules/analytics/gtmEventTypes/gtmEventType';
import { MarketingAnalyticsEventType } from '~/modules/analytics/marketingAnalyticsEventTypes/marketingAnalyticsEventTypes';
import { IS_DEVELOPMENT } from '~/modules/config';
import { AnalyticsEvent } from '~/modules/services/analyticsService';
import { UserState, userStore } from '~/modules/stores/userStore';
import { DataCloudEventType } from '~/modules/analytics/datacloudEventTypes/datacloudEventType';

export interface AnalyticsQueueEvent {
    eventType: MarketingAnalyticsEventType | GtmEventType | DataCloudEventType;
    event: AnalyticsEvent;
    target: AnalyticsTarget;
}

export interface AnalyticsQueueState extends State {
    analyticsEventsQueue: AnalyticsQueueEvent[];
    isUserProfileLoading: boolean;
    isDataCloudSdkLoading: boolean;
}

const initialState = {
    analyticsEventsQueue: [],
    isUserProfileLoading: true,
    isDataCloudSdkLoading: false,
};

export const analyticsQueueStore = createStore(
    initialState,
    (set: SetState<AnalyticsQueueState>) => ({
        sendAnalyticsEvent(analyticsQueueEvent: AnalyticsQueueEvent) {
            const { isUserProfileLoading, isDataCloudSdkLoading } = analyticsQueueStore.getState();
            if (isUserProfileLoading || isDataCloudSdkLoading) {
                set(function sendAnalyticsEvent(draft) {
                    draft.analyticsEventsQueue.push(analyticsQueueEvent);
                });
            } else {
                analyticsQueueEvent?.target?.sendAnalyticsEvent?.(analyticsQueueEvent);
            }
        },

        sendAllAnalyticsEvents() {
            const analyticsQueueEvents: AnalyticsQueueEvent[] = analyticsQueueStore.getState().analyticsEventsQueue;
            const isUserAuthenticated = !userStore.getState().isGuestUser;
            for (const analyticsQueueEvent of analyticsQueueEvents) {
                const analyticsEventWithAuthContext = {
                    ...analyticsQueueEvent,
                    event: {
                        ...analyticsQueueEvent.event,
                        authenticated: isUserAuthenticated,
                    },
                };
                analyticsQueueEvent?.target?.sendAnalyticsEvent?.(analyticsEventWithAuthContext);
            }
            set(function sendAllAnalyticsEvents(draft) {
                draft.analyticsEventsQueue = [];
            });
        },

        flushIfReady() {
            const { isUserProfileLoading, isDataCloudSdkLoading } = analyticsQueueStore.getState();
            if (!isUserProfileLoading && !isDataCloudSdkLoading) {
                analyticsQueueStore.sendAllAnalyticsEvents();
            }
        },

        setIsUserProfileLoading(isLoading: boolean) {
            set(function setIsUserProfileLoading(draft) {
                draft.isUserProfileLoading = isLoading;
            });
            analyticsQueueStore.flushIfReady();
        },

        setIsDataCloudSdkLoading(isLoading: boolean) {
            set(function setIsDataCloudSdkLoading(draft) {
                draft.isDataCloudSdkLoading = isLoading;
            });
            analyticsQueueStore.flushIfReady();
        },

        reset() {
            set(function reset() {
                return initialState;
            });
        },
    }),
    'analyticsQueueStore',
    IS_DEVELOPMENT,
);

userStore.subscribe(
    (isUserProfileLoading: boolean) => {
        // setIsUserProfileLoading flushes internally once both gates are clear.
        analyticsQueueStore.setIsUserProfileLoading(isUserProfileLoading);
    },
    (state: UserState) => state.isLoading,
);
